import React from 'react';
import { Link } from 'react-router-dom';
import { Button, Typography, Container, Box, Stack, Divider } from '@mui/material';
import { Camera, CloudUpload, Storefront, ArrowForward, LooksOne, LooksTwo, Looks3 } from '@mui/icons-material';

const features = [
  {
    icon: <Camera sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />,
    title: 'Photographer Ready',
    description: "You bring your favorite photographer. We provide the platform for them to easily upload all the race photos.",
  },
  {
    icon: <CloudUpload sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />,
    title: 'We Do The Rest',
    description: "Once photos are uploaded, our automated system gets to work. We handle bib number tagging, photo organization, and secure hosting.",
  },
  {
    icon: <Storefront sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />,
    title: 'Sell Your Photos',
    description: "We provide a simple storefront for each race, allowing participants to easily find and purchase their photos. You set the price, we handle the transaction.",
  },
];

const steps = [
    {
        icon: <LooksOne color="primary" sx={{ fontSize: 40, mr: 2 }}/>,
        title: "You Hire",
        description: "Your race hires the photographer. They handle taking all the great shots on race day."
    },
    {
        icon: <LooksTwo color="primary" sx={{ fontSize: 40, mr: 2 }}/>,
        title: "They Upload",
        description: "Your photographer uploads the photos directly to our platform. It's a simple drag-and-drop process."
    },
    {
        icon: <Looks3 color="primary" sx={{ fontSize: 40, mr: 2 }}/>,
        title: "We Deliver",
        description: "We automatically tag photos with bib numbers, host them, and provide a beautiful gallery for your runners."
    }
]

function HomePage() {
  return (
    <Box sx={{ flexGrow: 1, backgroundColor: 'white' }}>
      {/* Hero Section */}
      <Box sx={{ backgroundColor: 'primary.dark', color: 'white', py: 10, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h2" component="h1" fontWeight="700" gutterBottom>
            Effortless Race Photography
          </Typography>
          <Typography variant="h5" component="p" sx={{ mb: 4, color: 'grey.300' }}>
            You provide the photographer, we do the rest: tag, host, and sell. It's that simple.
          </Typography>
          <Button variant="outlined" color="inherit" component={Link} to="/race-search" size="large" sx={{ py: 1.5, px: 4, fontSize: '1.2rem', color: 'white', borderColor: 'white' }}>
            Find Your Photos
          </Button>
        </Container>
      </Box>

      {/* How It Works Section */}
      <Container sx={{ py: 8 }} maxWidth="md">
        <Typography variant="h4" component="h2" align="center" fontWeight="600" gutterBottom sx={{ mb: 8 }}>
          How It Works
        </Typography>
        <Stack divider={<Divider orientation="horizontal" flexItem />} spacing={4}>
            {steps.map((step, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
                    {step.icon}
                    <Box>
                        <Typography variant="h6" fontWeight="600">{step.title}</Typography>
                        <Typography color="text.secondary">{step.description}</Typography>
                    </Box>
                </Box>
            ))}
        </Stack>
      </Container>

      {/* Features Section */}
      <Box sx={{ backgroundColor: 'grey.50', py: 8 }}>
        <Container maxWidth="md">
          <Typography variant="h4" component="h2" align="center" fontWeight="600" gutterBottom sx={{ mb: 8 }}>
            Everything You Need
          </Typography>
          <Stack divider={<Divider orientation="horizontal" flexItem />} spacing={4}>
            {features.map((feature, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
                {feature.icon}
                <Box>
                  <Typography variant="h6" component="h3" fontWeight="600">
                    {feature.title}
                  </Typography>
                  <Typography color="text.secondary">
                    {feature.description}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* Call to Action */}
      <Container sx={{ py: 8, textAlign: 'center' }} maxWidth="md">
        <Typography variant="h4" component="h2" fontWeight="600" gutterBottom>
          Ready to Simplify Your Race Photography?
        </Typography>
        <Button variant="contained" color="primary" component={Link} to="/register" size="large" endIcon={<ArrowForward />} sx={{ mt: 2, py: 1.5, px: 5, fontSize: '1.2rem' }}>
          Create a Race Director Account
        </Button>
      </Container>

      {/* Footer */}
      <Box component="footer" sx={{ backgroundColor: 'primary.dark', color: 'white', py: 4, textAlign: 'center' }}>
        <Typography variant="body1">
          © 2025 Race Photos. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
}

export default HomePage;
